// In Next.js, this file would be called: app/providers.jsx
'use client';

import {
  isServer,
  QueryClient,
  QueryClientProvider as TanstackQueryClientProvider,
} from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { FC, PropsWithChildren } from 'react';
// Since QueryClientProvider relies on useContext under the hood, we have to put 'use client' on top
import { createQueryClientWithCorsHandler } from '@/services/setup-cors-error-handler';

// `@tanstack/react-query-devtools` is a heavy dev-only bundle (hundreds of KB).
// Importing it statically pulls it into the production client graph and shows
// up in Lighthouse "Duplicated/Legacy JavaScript" insights. We dynamically
// import it ONLY in development so it is fully tree-shaken in production.
const ReactQueryDevtools =
  process.env.NODE_ENV === 'production'
    ? () => null
    : dynamic(() => import('@tanstack/react-query-devtools').then(m => m.ReactQueryDevtools), {
        ssr: false,
      });

function makeQueryClient() {
  return createQueryClientWithCorsHandler();
}

let browserQueryClient: QueryClient | undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

const QueryClientProvider: FC<PropsWithChildren> = ({ children }) => {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();

  return (
    <TanstackQueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </TanstackQueryClientProvider>
  );
};

export default QueryClientProvider;
